import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { AlertCircle, TrendingUp, TrendingDown, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${API}/api`, withCredentials: true });

const lastMondayMidnight = () => {
  const now = new Date();
  const daysSinceMon = (now.getDay() + 6) % 7;
  const d = new Date(now);
  d.setDate(now.getDate() - daysSinceMon);
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatCompact = (n) => {
  if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
  return String(n);
};

const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

const formatDateShort = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const formatDateFull = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
};

// Mini sparkline SVG
function Sparkline({ data }) {
  if (!data || data.length < 2) return null;
  const w = 56, h = 18, pad = 2;
  const vals = data.map(d => d.credits);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const points = vals.map((v, i) => {
    const x = pad + (i / (vals.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  }).join(' ');
  const lastPt = points.split(' ').pop().split(',');
  return (
    <svg width={w} height={h} className="inline-block ml-1.5">
      <polyline points={points} fill="none" stroke="#2EB4AC" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={lastPt[0]} cy={lastPt[1]} r="2.5" fill="#2EB4AC" />
    </svg>
  );
}

export default function CostTrackerWidget() {
  const [state, setState] = useState(null);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [credits, setCredits] = useState('');
  const [rate, setRate] = useState('');
  const [saving, setSaving] = useState(false);
  const pillRef = useRef(null);

  const fetchState = useCallback(async () => {
    try {
      const res = await api.get('/cost-tracker');
      setState(res.data);
    } catch (err) { console.error('Cost tracker fetch error:', err.message); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await api.get('/cost-tracker/history?weeks=12');
      setHistory(res.data.history || []);
    } catch (err) { console.error('Cost tracker history error:', err.message); }
  }, []);

  useEffect(() => { fetchState(); fetchHistory(); }, [fetchState, fetchHistory]);

  // Click outside pill → collapse
  useEffect(() => {
    if (!expanded) return;
    const handler = (e) => {
      if (pillRef.current && !pillRef.current.contains(e.target)) setExpanded(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [expanded]);

  const stale = !state?.updated_at || new Date(state.updated_at) < lastMondayMidnight();
  const totalCredits = state?.total_credits || 0;
  const costPerCredit = state?.cost_per_credit_inr || 13.70;
  const totalSpent = totalCredits * costPerCredit;

  const handleOpenDialog = () => {
    setCredits(String(totalCredits || ''));
    setRate(String(costPerCredit || '13.70'));
    setDialogOpen(true);
    setExpanded(false);
  };

  const handleSave = async () => {
    const c = parseFloat(credits);
    const r = parseFloat(rate);
    if (isNaN(c) || isNaN(r)) return;
    setSaving(true);
    try {
      await api.put('/cost-tracker', { total_credits: c, cost_per_credit_inr: r });
      toast.success('Cost tracker updated');
      setDialogOpen(false);
      await fetchState();
      await fetchHistory();
    } catch (err) {
      console.error(err);
      toast.error('Failed to update');
    }
    setSaving(false);
  };

  // Trend delta
  const trendDelta = history.length >= 2 ? (() => {
    const curr = history[history.length - 1].credits;
    const prev = history[history.length - 2].credits;
    const diff = curr - prev;
    const pct = prev ? ((diff / prev) * 100).toFixed(0) : 0;
    return { diff, pct, up: diff >= 0 };
  })() : null;

  const livePreview = (() => {
    const c = parseFloat(credits) || 0;
    const r = parseFloat(rate) || 13.70;
    return c * r;
  })();

  // ─── Collapsed state: icon button ───
  if (!expanded && !dialogOpen) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="relative h-10 w-10 flex items-center justify-center rounded-md hover:bg-[#F4F4F4] transition-colors"
        data-testid="cost-tracker-icon-btn"
      >
        <img src="/icons/cost-rupee.png" alt="Cost" className="h-7 w-7 hover:scale-110 transition-transform" />
        {stale && (
          <span className="absolute top-1 right-1 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border border-white" />
          </span>
        )}
      </button>
    );
  }

  // ─── Expanded pill ───
  if (expanded && !dialogOpen) {
    return (
      <div ref={pillRef} onClick={handleOpenDialog} className="h-11 flex items-center rounded-xl border border-slate-200 bg-white cursor-pointer animate-in fade-in slide-in-from-right-2 duration-200" data-testid="cost-tracker-pill">
        {/* Left cell */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-50/50 rounded-l-xl h-full">
          <img src="/icons/cost-rupee.png" alt="" className="h-5 w-5" />
          <div className="flex flex-col">
            <span className="text-[9px] font-semibold uppercase text-slate-400 tracking-wider leading-none">Credits</span>
            <span className="text-sm font-bold tabular-nums text-[#0F0F0F] leading-tight">{formatCompact(totalCredits)}</span>
          </div>
        </div>
        {/* Divider */}
        <div className="w-px h-6 bg-slate-200" />
        {/* Right cell */}
        <div className="flex items-center gap-1.5 px-3 py-1.5 h-full">
          <div className="flex flex-col">
            <span className="text-[9px] font-semibold uppercase text-[#2EB4AC] tracking-wider leading-none">Spent</span>
            <span className="text-sm font-bold tabular-nums text-[#0F0F0F] leading-tight">{formatINR(totalSpent)}</span>
          </div>
          <Sparkline data={history} />
          {stale ? (
            <span className="flex h-2 w-2 ml-1">
              <span className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
            </span>
          ) : (
            <Pencil size={12} className="text-slate-300 ml-1 hover:text-slate-500 transition-colors" />
          )}
        </div>
      </div>
    );
  }

  // ─── Dialog (always renders for controlled open/close) ───
  return (
    <>
      <button
        onClick={() => setExpanded(true)}
        className="relative h-10 w-10 flex items-center justify-center rounded-md hover:bg-[#F4F4F4] transition-colors"
        data-testid="cost-tracker-icon-btn"
      >
        <img src="/icons/cost-rupee.png" alt="Cost" className="h-7 w-7" />
        {stale && (
          <span className="absolute top-1 right-1 flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border border-white" />
          </span>
        )}
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="cost-tracker-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-bold">
              <img src="/icons/cost-rupee.png" alt="" className="h-6 w-6" />
              App Development Cost Tracker
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Stale alert */}
            {stale && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200" data-testid="cost-tracker-stale-alert">
                <AlertCircle size={16} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800">It's a new week — please update the credits consumed since last Monday.</p>
              </div>
            )}

            {/* Trendline chart */}
            {history.length >= 2 ? (
              <div className="border border-[#E8E8E8] rounded-md p-4" data-testid="cost-tracker-chart">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#555555]">Week-on-week credits</p>
                    <p className="text-[10px] text-[#555555]">Last {history.length} weeks · Mondays</p>
                  </div>
                  {trendDelta && (
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-0.5 ${trendDelta.up ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                      {trendDelta.up ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                      {trendDelta.diff > 0 ? '+' : ''}{formatCompact(trendDelta.diff)} cr ({trendDelta.pct}%)
                    </span>
                  )}
                </div>
                <ResponsiveContainer width="100%" height={176}>
                  <AreaChart data={history} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2EB4AC" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#2EB4AC" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E8E8E8" />
                    <XAxis dataKey="week_start" tickFormatter={formatDateShort} tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} />
                    <YAxis tickFormatter={formatCompact} tick={{ fontSize: 10, fill: '#555' }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ borderRadius: 12, fontSize: 12, border: '1px solid #E8E8E8' }}
                      formatter={(val) => [formatCompact(val) + ' credits', 'Credits']}
                      labelFormatter={formatDateShort}
                    />
                    <Area type="monotone" dataKey="credits" stroke="#2EB4AC" strokeWidth={2} fill="url(#costGrad)" dot={{ r: 3, fill: '#fff', stroke: '#2EB4AC', strokeWidth: 2 }} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="border border-dashed border-slate-300 rounded-md p-6 text-center" data-testid="cost-tracker-empty-chart">
                <p className="text-xs text-[#555555]">Save updates each week to build the trendline.</p>
              </div>
            )}

            {/* Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Total Credits</Label>
                <Input data-testid="cost-credits-input" type="number" value={credits} onChange={e => setCredits(e.target.value)} placeholder="0" className="border-[#E8E8E8] rounded-md h-10" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#555555] mb-1.5 block">Cost per Credit (₹)</Label>
                <Input data-testid="cost-rate-input" type="number" step="0.01" value={rate} onChange={e => setRate(e.target.value)} placeholder="13.70" className="border-[#E8E8E8] rounded-md h-10" />
                <p className="text-[10px] text-[#555555] mt-1">Default ₹13.70 — edit anytime as billing changes.</p>
              </div>
            </div>

            {/* Live preview */}
            <div className="bg-slate-50/60 rounded-md p-4 text-center" data-testid="cost-tracker-preview">
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#555555] mb-1">Estimated Total Cost</p>
              <p className="text-2xl font-bold text-[#0F0F0F] font-['Manrope']">{formatINR(livePreview)}</p>
            </div>

            {/* Last updated */}
            {state?.updated_at && (
              <p className="text-[10px] text-[#555555] text-center">
                Last updated {formatDateFull(state.updated_at)} by {state.updated_by?.split('@')[0] || 'unknown'}
              </p>
            )}
          </div>

          <DialogFooter className="gap-2">
            <button onClick={() => setDialogOpen(false)} className="h-9 px-4 text-sm border border-[#E8E8E8] rounded-md hover:bg-[#F4F4F4] transition-colors" data-testid="cost-cancel-btn">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="h-9 px-4 text-sm font-semibold text-white bg-[#2EB4AC] hover:bg-[#259993] rounded-md disabled:opacity-50 transition-colors" data-testid="cost-save-btn">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
